import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Gift,
  Copy,
  Check,
  CheckCircle,
  Share2,
  Mail,
  UserPlus,
  Send,
  X,
  Clock,
  Users,
  Loader2,
  Sparkles,
  AlertCircle,
  Trophy,
  Star,
  Upload,
  Image as ImageIcon,
  Eye,
  Code
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default logo URL
const DEFAULT_LOGO = '/mpm-logo.png';

// Generate HTML signature for external email clients
const generateSignatureHTML = (referralCode, displayName, avatarUrl = null) => {
  const signupUrl = `${window.location.origin}/register?ref=${referralCode}`;
  const imageUrl = avatarUrl || DEFAULT_LOGO;
  const imageStyle = avatarUrl 
    ? 'border-radius: 50%; object-fit: cover;' 
    : 'border-radius: 8px;';
  
  return `<!-- MyPatriotMail Email Signature -->
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; max-width: 500px;">
  <tr>
    <td style="padding: 15px 0; border-top: 3px solid #6D4AFF;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td style="vertical-align: top; padding-right: 15px;">
            <img src="${imageUrl}" alt="${displayName}" width="60" height="60" style="${imageStyle}" />
          </td>
          <td style="vertical-align: top;">
            <p style="margin: 0 0 5px 0; font-size: 16px; font-weight: bold; color: #1a1a2e;">
              ${displayName}
            </p>
            <p style="margin: 0 0 10px 0; font-size: 13px; color: #6D4AFF; font-weight: 600;">
              🦅 Proud MyPatriotMail User
            </p>
            <p style="margin: 0; font-size: 12px; color: #666666; line-height: 1.5;">
              Join me on the most secure, American-owned email platform.<br/>
              <strong style="color: #1a1a2e;">Your privacy matters. Your data is never sold.</strong>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding: 12px 0;">
      <table cellpadding="0" cellspacing="0" border="0" style="background: linear-gradient(135deg, #6D4AFF 0%, #5B3FD9 100%); border-radius: 8px;">
        <tr>
          <td style="padding: 12px 20px;">
            <a href="${signupUrl}" style="text-decoration: none; color: #ffffff; font-size: 14px; font-weight: bold; display: inline-block;">
              🛡️ Join MyPatriotMail FREE →
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding: 8px 0 0 0;">
      <p style="margin: 0; font-size: 11px; color: #999999;">
        Use my referral code: <strong style="color: #6D4AFF; font-family: monospace; background: #f0f0f0; padding: 2px 6px; border-radius: 4px;">${referralCode}</strong>
      </p>
    </td>
  </tr>
  <tr>
    <td style="padding: 10px 0 0 0; border-top: 1px solid #eeeeee; margin-top: 10px;">
      <p style="margin: 0; font-size: 10px; color: #bbbbbb;">
        🇺🇸 American-owned • 🔒 End-to-end encrypted • 🚫 We never sell your data
      </p>
    </td>
  </tr>
</table>
<!-- End MyPatriotMail Signature -->`;
};

export default function ReferralDashboardPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const avatarInputRef = useRef(null);

  // Invite status state
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [emails, setEmails] = useState(['']);
  const [personalMessage, setPersonalMessage] = useState('');
  const [errors, setErrors] = useState({});
  
  // Copy states
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [signatureCopied, setSignatureCopied] = useState(false);
  
  // Signature states
  const [showPreview, setShowPreview] = useState(false);
  const [signatureAvatar, setSignatureAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Email preview state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Theme
  const { isDarkMode } = useTheme();
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-gray-50';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  // Generate email preview HTML
  const generateEmailPreviewHTML = () => {
    const displayName = user?.display_name || user?.email?.split('@')[0] || 'User';
    const userEmail = user?.email || 'you@mypatriotmail.org';
    const refCode = inviteStatus?.referral_code || user?.referral_code || 'XXXXXXXX';
    const logoUrl = `${window.location.origin}/mpm-logo.png`;
    const signupUrl = `${window.location.origin}/register?ref=${refCode}`;
    const sampleMessage = personalMessage || '';
    
    const messageSection = sampleMessage ? `
      <tr>
        <td style="padding: 25px 30px; background: linear-gradient(135deg, rgba(109, 74, 255, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%); border-left: 4px solid #6D4AFF; border-radius: 0 12px 12px 0; margin: 25px 0;">
          <p style="margin: 0 0 8px 0; color: #6D4AFF; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; font-weight: 600;">💬 Personal Message</p>
          <p style="margin: 0; color: #ffffff; font-size: 16px; line-height: 1.7; font-style: italic;">"${sampleMessage}"</p>
          <p style="margin: 12px 0 0 0; color: #a0a0b0; font-size: 13px;">— ${displayName}</p>
        </td>
      </tr>
      <tr><td style="height: 25px;"></td></tr>
    ` : '';
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #0a0a14;">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background-color: #0a0a14;">
    <tr>
      <td align="center" style="padding: 20px 10px;">
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="max-width: 500px; background-color: #12121f; border-radius: 16px; overflow: hidden; box-shadow: 0 15px 30px -8px rgba(0,0,0,0.5), 0 0 0 1px rgba(109, 74, 255, 0.2);">
          
          <!-- Hero Header with Logo -->
          <tr>
            <td style="background: linear-gradient(180deg, #1a1a2e 0%, #12121f 100%); padding: 30px 20px 25px 20px; text-align: center; border-bottom: 1px solid rgba(109, 74, 255, 0.3);">
              <img src="${logoUrl}" alt="MyPatriotMail" width="60" height="60" style="display: block; margin: 0 auto 15px auto; border-radius: 12px; box-shadow: 0 8px 30px rgba(109, 74, 255, 0.3);" />
              <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: -0.5px;">MyPatriotMail</h1>
              <p style="margin: 6px 0 0 0; color: #6D4AFF; font-size: 11px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase;">Secure • Private • American</p>
            </td>
          </tr>
          
          <!-- Invitation Banner -->
          <tr>
            <td style="background: linear-gradient(135deg, #6D4AFF 0%, #8B5CF6 50%, #6D4AFF 100%); padding: 20px; text-align: center;">
              <p style="margin: 0; color: rgba(255,255,255,0.95); font-size: 13px; text-transform: uppercase; letter-spacing: 2px;">🎉 You've Been Invited! 🎉</p>
            </td>
          </tr>
          
          <!-- Main Content -->
          <tr>
            <td style="padding: 30px 25px;">
              
              <!-- Inviter Info -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 25px;">
                <tr>
                  <td style="text-align: center;">
                    <div style="display: inline-block; background: linear-gradient(135deg, #252540 0%, #1e1e35 100%); border-radius: 12px; padding: 18px 28px; border: 1px solid rgba(109, 74, 255, 0.2);">
                      <p style="margin: 0 0 3px 0; color: #a0a0b0; font-size: 11px;">Invited by</p>
                      <p style="margin: 0; color: #ffffff; font-size: 17px; font-weight: 700;">${displayName}</p>
                      <p style="margin: 4px 0 0 0; color: #6D4AFF; font-size: 12px;">${userEmail}</p>
                    </div>
                  </td>
                </tr>
              </table>
              
              <p style="margin: 0 0 20px 0; color: #c0c0d0; font-size: 14px; line-height: 1.6; text-align: center;">
                Your friend thinks you'd be perfect for <strong style="color: #ffffff;">MyPatriotMail</strong> — the secure, encrypted email platform built for privacy-conscious Americans.
              </p>
              
              ${messageSection}
              
              <!-- Referral Code Box -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin: 20px 0;">
                <tr>
                  <td style="padding: 22px; background: linear-gradient(135deg, #1a1a2e 0%, #252540 100%); border: 2px solid #6D4AFF; border-radius: 12px; text-align: center; box-shadow: 0 0 25px rgba(109, 74, 255, 0.15);">
                    <p style="margin: 0 0 8px 0; color: #6D4AFF; font-size: 10px; text-transform: uppercase; letter-spacing: 2px; font-weight: 600;">🎁 Your Exclusive Referral Code</p>
                    <p style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 800; letter-spacing: 5px; text-shadow: 0 0 15px rgba(109, 74, 255, 0.4);">${refCode}</p>
                    <p style="margin: 10px 0 0 0; color: #a0a0b0; font-size: 11px;">Enter this code when you sign up</p>
                  </td>
                </tr>
              </table>
              
              <!-- CTA Button -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td align="center" style="padding: 12px 0 25px 0;">
                    <a href="${signupUrl}" style="display: inline-block; background: linear-gradient(135deg, #6D4AFF 0%, #8B5CF6 100%); color: #ffffff; text-decoration: none; padding: 16px 36px; border-radius: 50px; font-weight: 700; font-size: 15px; box-shadow: 0 8px 25px rgba(109, 74, 255, 0.35); letter-spacing: 0.3px;">
                      🚀 Join MyPatriotMail Free
                    </a>
                  </td>
                </tr>
              </table>
              
              <!-- Features Grid -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                <tr>
                  <td style="padding: 15px; background-color: #1a1a2e; border-radius: 10px; border: 1px solid #2d2d44;">
                    <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                      <tr>
                        <td width="33%" style="text-align: center; padding: 10px 5px;">
                          <p style="margin: 0; font-size: 24px;">🔐</p>
                          <p style="margin: 6px 0 0 0; color: #ffffff; font-weight: 600; font-size: 11px;">Encrypted</p>
                        </td>
                        <td width="33%" style="text-align: center; padding: 10px 5px; border-left: 1px solid #2d2d44; border-right: 1px solid #2d2d44;">
                          <p style="margin: 0; font-size: 24px;">🚫</p>
                          <p style="margin: 6px 0 0 0; color: #ffffff; font-weight: 600; font-size: 11px;">No Tracking</p>
                        </td>
                        <td width="33%" style="text-align: center; padding: 10px 5px;">
                          <p style="margin: 0; font-size: 24px;">🇺🇸</p>
                          <p style="margin: 6px 0 0 0; color: #ffffff; font-weight: 600; font-size: 11px;">American</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
              
              <!-- Trust Badges -->
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin-top: 18px;">
                <tr>
                  <td style="text-align: center;">
                    <p style="margin: 0; color: #6c757d; font-size: 10px;">✓ Free to join &nbsp;•&nbsp; ✓ No credit card &nbsp;•&nbsp; ✓ Cancel anytime</p>
                  </td>
                </tr>
              </table>
              
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 25px 20px; background: linear-gradient(180deg, #0f0f1a 0%, #0a0a12 100%); text-align: center; border-top: 1px solid #2d2d44;">
              <p style="margin: 0 0 8px 0; color: #6D4AFF; font-size: 12px; font-weight: 600;">MyPatriotMail</p>
              <p style="margin: 0; color: #4a4a5a; font-size: 10px;">© 2026 MyPatriotMail. All rights reserved.</p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  useEffect(() => {
    fetchInviteStatus();
  }, []);

  const fetchInviteStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invites/status`);
      setInviteStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch invite status:', err);
      toast.error('Failed to load invite status');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
    
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const addEmailField = () => {
    if (emails.length < (inviteStatus?.remaining_this_week || 10)) {
      setEmails([...emails, '']);
    }
  };

  const removeEmailField = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  const copyReferralCode = () => {
    const code = inviteStatus?.referral_code || user?.referral_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setCodeCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    const code = inviteStatus?.referral_code || user?.referral_code;
    if (code) {
      navigator.clipboard.writeText(`${window.location.origin}/register?ref=${code}`);
      setLinkCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const copySignatureHTML = () => {
    const code = inviteStatus?.referral_code || user?.referral_code || 'PATRIOT';
    const name = user?.display_name || user?.email?.split('@')[0] || 'Patriot';
    const html = generateSignatureHTML(code, name, signatureAvatar);
    navigator.clipboard.writeText(html);
    setSignatureCopied(true);
    toast.success('Signature HTML copied to clipboard!');
    setTimeout(() => setSignatureCopied(false), 3000);
  };

  const handleSendInvites = async () => {
    const newErrors = {};
    const validEmails = [];
    
    emails.forEach((email, index) => {
      const trimmed = email.trim();
      if (trimmed) {
        if (!validateEmail(trimmed)) {
          newErrors[index] = 'Invalid email format';
        } else if (trimmed.endsWith('@mypatriotmail.org')) {
          newErrors[index] = 'Cannot invite MyPatriotMail users';
        } else {
          validEmails.push(trimmed);
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (validEmails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/invites/send-bulk`, {
        emails: validEmails,
        personal_message: personalMessage.trim() || null
      });

      const sentCount = response.data.sent;
      toast.success(`${sentCount} invitation${sentCount !== 1 ? 's' : ''} sent successfully!`);
      
      setEmails(['']);
      setPersonalMessage('');
      setErrors({});
      fetchInviteStatus();
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send invitations';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-blue-500/20 text-blue-400"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'registered':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><UserPlus className="w-3 h-3 mr-1" />Signed Up</Badge>;
      case 'converted':
        return <Badge className="bg-green-500/20 text-green-400"><Check className="w-3 h-3 mr-1" />Paid</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>;
    }
  };

  const referralCode = inviteStatus?.referral_code || user?.referral_code || 'LOADING';
  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

  if (loading) {
    return (
      <div className={`min-h-screen ${bgPrimary} flex items-center justify-center`}>
        <Loader2 className="w-10 h-10 animate-spin text-[#6D4AFF]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgPrimary} py-8 px-4 sm:px-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className={`text-gray-400 hover:${textPrimary} hover:bg-[#2d2d44] p-2`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${textPrimary}`}>Referral Dashboard</h1>
            <p className={textMuted + " text-sm"}>Invite friends, earn rewards, and grow the community</p>
          </div>
        </div>

        {/* Main Content - Two Column Layout 70/30 */}
        <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-6">
          
          {/* LEFT COLUMN - Email Signature Invite */}
          <div className="space-y-6">
            <div className={`${bgSecondary} rounded-2xl border ${borderColor} p-6`}>
              {/* Your Referral Link - AT THE TOP */}
              <div className="p-4 bg-gradient-to-r from-[#6D4AFF]/20 to-purple-500/10 rounded-xl border border-[#6D4AFF]/30 mb-6">
                <Label className={`${textSecondary} flex items-center gap-2 mb-2`}>
                  <Share2 className="w-4 h-4 text-[#6D4AFF]" />
                  Your Referral Link
                </Label>
                <div className="flex items-center gap-2">
                  <div className={`flex-1 ${bgTertiary} rounded-lg px-3 py-2 ${borderColor}`}>
                    <code className="text-sm text-[#6D4AFF] break-all">
                      {referralLink}
                    </code>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyReferralLink}
                    className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF]/10 flex-shrink-0"
                    data-testid="copy-referral-link-btn"
                  >
                    {linkCopied ? <CheckCircle className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Share this link with friends. When they sign up and pay, you earn rewards!
                </p>
              </div>

              {/* Email Signature Invite Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-gradient-to-br from-[#6D4AFF]/20 to-purple-500/10">
                  <Mail className="w-5 h-5 text-[#6D4AFF]" />
                </div>
                <div>
                  <h2 className={`text-lg font-bold ${textPrimary}`}>Email Signature Invite</h2>
                  <p className={`text-sm ${textMuted}`}>Add this signature to your Gmail, Outlook, or other email accounts</p>
                </div>
              </div>

              {/* Signature Photo Upload */}
              <div className={`p-4 ${bgTertiary} rounded-xl border ${borderColor} mb-6`}>
                <Label className={`${textSecondary} flex items-center gap-2 mb-3`}>
                  <ImageIcon className="w-4 h-4 text-[#6D4AFF]" />
                  Signature Photo
                </Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#6D4AFF]/50 bg-[#1a1a2e] flex items-center justify-center">
                      {signatureAvatar ? (
                        <img src={signatureAvatar} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <img src={DEFAULT_LOGO} alt="Default" className="w-10 h-10" />
                      )}
                    </div>
                    {signatureAvatar && (
                      <button
                        onClick={() => setSignatureAvatar(null)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${textMuted} mb-2`}>
                      {signatureAvatar ? 'Your custom photo will appear in your signature' : 'Upload a photo or use the default eagle logo'}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF]/10"
                    >
                      {uploadingAvatar ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Photo
                    </Button>
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('Image must be less than 2MB');
                            return;
                          }
                          setUploadingAvatar(true);
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setSignatureAvatar(reader.result);
                            setUploadingAvatar(false);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Preview Toggle */}
              <div className={`flex items-center justify-between p-3 ${bgTertiary} rounded-lg mb-4`}>
                <div className="flex items-center gap-2">
                  <Eye className={`w-4 h-4 ${textMuted}`} />
                  <span className={textSecondary}>Preview Signature</span>
                </div>
                <Switch checked={showPreview} onCheckedChange={setShowPreview} />
              </div>

              {/* Signature Preview */}
              {showPreview && (
                <div className="p-6 bg-white rounded-xl mb-4">
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(generateSignatureHTML(
                        referralCode,
                        user?.display_name || user?.email?.split('@')[0] || 'Patriot',
                        signatureAvatar
                      ))
                    }} 
                  />
                </div>
              )}

              {/* HTML Signature Code */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className={`${textSecondary} flex items-center gap-2`}>
                    <Code className="w-4 h-4" />
                    HTML Signature Code
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copySignatureHTML}
                    className={signatureCopied ? 'text-green-500' : 'text-[#6D4AFF]'}
                  >
                    {signatureCopied ? <><CheckCircle className="w-4 h-4 mr-2" />Copied!</> : <><Copy className="w-4 h-4 mr-2" />Copy HTML</>}
                  </Button>
                </div>
                <pre className={`p-4 ${isDarkMode ? 'bg-[#0d0d1a]' : 'bg-gray-50'} rounded-xl border ${borderColor} text-xs ${textMuted} overflow-x-auto max-h-[200px] overflow-y-auto font-mono`}>
                  {generateSignatureHTML(
                    referralCode,
                    user?.display_name || user?.email?.split('@')[0] || 'Patriot',
                    signatureAvatar
                  )}
                </pre>
              </div>

              {/* Instructions */}
              <div className="mt-6 space-y-4">
                <h3 className={`${textPrimary} font-semibold flex items-center gap-2`}>
                  <AlertCircle className="w-4 h-4 text-[#6D4AFF]" />
                  How to Add to Your Email
                </h3>
                
                <div className="grid gap-4">
                  <div className={`p-4 ${bgTertiary} rounded-lg`}>
                    <h4 className={`${textPrimary} font-medium mb-2`}>📧 Gmail</h4>
                    <ol className={`text-sm ${textMuted} space-y-1 list-decimal list-inside`}>
                      <li>Go to Gmail Settings (gear icon) → See all settings</li>
                      <li>Scroll to &ldquo;Signature&rdquo; section</li>
                      <li>Click &ldquo;Create new&rdquo; or edit existing</li>
                      <li>Paste the HTML code</li>
                      <li>Click &ldquo;Save Changes&rdquo;</li>
                    </ol>
                  </div>
                  
                  <div className={`p-4 ${bgTertiary} rounded-lg`}>
                    <h4 className={`${textPrimary} font-medium mb-2`}>📨 Outlook</h4>
                    <ol className={`text-sm ${textMuted} space-y-1 list-decimal list-inside`}>
                      <li>Go to Settings → View all Outlook settings</li>
                      <li>Select Mail → Compose and reply</li>
                      <li>Scroll to &ldquo;Email signature&rdquo;</li>
                      <li>Paste the HTML code</li>
                      <li>Click &ldquo;Save&rdquo;</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Referral Code & Stats - MOVED TO BOTTOM */}
              <div className={`p-4 ${bgTertiary} rounded-xl border ${borderColor}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${textMuted} mb-1`}>Your Referral Code</p>
                    <p className={`text-2xl font-bold ${textPrimary} font-mono tracking-wider`}>
                      {referralCode}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${textMuted} mb-1`}>Paid Referrals</p>
                    <p className="text-xl font-bold text-[#6D4AFF]">
                      {inviteStatus?.converted || 0} <span className={`text-sm ${textMuted}`}>/ ∞</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Preview Modal */}
          <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
            <DialogContent className={`${isDarkMode ? 'bg-[#0a0a14]' : 'bg-white'} border ${borderColor} max-w-3xl max-h-[90vh] p-0 overflow-hidden`}>
              <div className={`${bgSecondary} px-6 py-4 border-b ${borderColor} flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className={`${textPrimary} font-semibold`}>Email Preview</h3>
                    <p className={`text-xs ${textMuted}`}>This is how your invite email will appear to recipients</p>
                  </div>
                </div>
              </div>
              
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
                <iframe
                  srcDoc={generateEmailPreviewHTML()}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ height: '800px' }}
                />
              </div>
              
              <div className={`${bgSecondary} px-6 py-3 border-t ${borderColor} flex items-center justify-between`}>
                <p className={`text-xs ${textMuted}`}>
                  💡 Personal message will be included if you add one before sending
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEmailPreview(false)}
                  className={`${borderColor} ${textMuted} hover:${bgTertiary}`}
                >
                  Close Preview
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* RIGHT COLUMN - Invite Friends & Earn Rewards */}
          <div className="space-y-6">
            <div className={`${bgSecondary} rounded-2xl border ${borderColor} p-6`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6]">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-lg font-bold ${textPrimary}`}>Invite Friends & Earn Rewards</h2>
                  <p className={`text-sm ${textMuted}`}>Send invitations and track your progress</p>
                </div>
              </div>

              {/* Email Inputs - MOVED TO TOP */}
              {inviteStatus?.remaining_this_week > 0 ? (
                <>
                  <div className="space-y-3 mb-4">
                    <label className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}>
                      <Mail className="w-4 h-4" />
                      Email Addresses
                    </label>
                    {emails.map((email, index) => (
                      <div key={index} className="flex gap-2">
                        <div className="flex-1">
                          <Input
                            type="email"
                            placeholder="friend@email.com"
                            value={email}
                            onChange={(e) => handleEmailChange(index, e.target.value)}
                            data-testid={`invite-email-input-${index}`}
                            className={`${bgTertiary} border ${borderColor} ${textPrimary} placeholder:${textMuted} ${
                              errors[index] ? 'border-red-500' : ''
                            }`}
                          />
                          {errors[index] && (
                            <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              {errors[index]}
                            </p>
                          )}
                        </div>
                        {emails.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEmailField(index)}
                            className={`${textMuted} hover:text-red-400 hover:bg-red-400/10`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    
                    {emails.length < inviteStatus?.remaining_this_week && (
                      <Button
                        variant="ghost"
                        onClick={addEmailField}
                        data-testid="add-another-email-btn"
                        className={`w-full border border-dashed ${borderColor} ${textMuted} hover:${textPrimary} hover:border-[#6D4AFF]`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Another Email
                      </Button>
                    )}
                  </div>

                  {/* Personal Message */}
                  <div className="space-y-2 mb-4">
                    <label className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}>
                      <Sparkles className="w-4 h-4" />
                      Personal Message (Optional)
                    </label>
                    <Textarea
                      placeholder="Add a personal note to make your invitation more meaningful..."
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      data-testid="invite-personal-message"
                      className={`${bgTertiary} border ${borderColor} ${textPrimary} placeholder:${textMuted} min-h-[80px] resize-none`}
                      maxLength={500}
                    />
                    <p className={`text-xs ${textMuted} text-right`}>{personalMessage.length}/500</p>
                  </div>

                  {/* Send Button */}
                  <Button
                    onClick={handleSendInvites}
                    disabled={sending}
                    data-testid="send-invitation-btn"
                    className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6] hover:from-[#5B3FD9] hover:to-[#7C4FE0] text-white py-6 mb-3"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending Invitations...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" />Send Invitation{emails.filter(e => e.trim()).length > 1 ? 's' : ''}</>
                    )}
                  </Button>
                  
                  {/* Email Preview Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailPreview(true)}
                    className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 mb-6"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Invite Email
                  </Button>
                </>
              ) : (
                <div className={`${bgTertiary} rounded-xl p-6 text-center mb-6`}>
                  <Clock className={`w-12 h-12 ${textMuted} mx-auto mb-3`} />
                  <p className={`${textSecondary} font-medium`}>Weekly limit reached</p>
                  <p className={`text-sm ${textMuted} mt-1`}>You can send more invites next week!</p>
                </div>
              )}

              {/* Weekly & Conversion Stats */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className={`${bgTertiary} rounded-xl p-4 text-center`}>
                  <div className={`text-2xl font-bold ${textPrimary}`}>{inviteStatus?.remaining_this_week || 0}</div>
                  <div className={`text-xs ${textMuted} mt-1`}>Left This Week</div>
                  <div className={`text-[10px] ${textMuted} mt-1`}>of {inviteStatus?.max_per_week || 10}</div>
                </div>
                <div className={`${bgTertiary} rounded-xl p-4 text-center`}>
                  <div className="text-2xl font-bold text-[#6D4AFF]">{inviteStatus?.total_invites || 0}</div>
                  <div className={`text-xs ${textMuted} mt-1`}>Total Sent</div>
                </div>
                <div className={`${bgTertiary} rounded-xl p-4 text-center`}>
                  <div className="text-2xl font-bold text-yellow-400">{inviteStatus?.registered || 0}</div>
                  <div className={`text-xs ${textMuted} mt-1`}>Signed Up</div>
                </div>
                <div className={`${bgTertiary} rounded-xl p-4 text-center`}>
                  <div className="text-2xl font-bold text-green-400">{inviteStatus?.converted || 0}</div>
                  <div className={`text-xs ${textMuted} mt-1`}>Paid Users</div>
                </div>
              </div>

              {/* Reward Progress */}
              <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl p-5 border border-amber-500/30 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Trophy className="w-6 h-6 text-amber-400" />
                  <div>
                    <p className={`${textPrimary} font-semibold`}>Referral Rewards</p>
                    <p className={`text-xs ${textMuted}`}>
                      {inviteStatus?.has_lifetime_reward ? '🎉 LIFETIME MEMBER!' : `${inviteStatus?.converted || 0} paid referrals`}
                    </p>
                  </div>
                </div>
                
                {inviteStatus?.has_lifetime_reward ? (
                  <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-lg p-4 text-center">
                    <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-400 font-bold text-lg">🎉 LIFETIME MEMBER</p>
                    <p className={`text-xs ${textSecondary} mt-1`}>You&apos;ve earned The Patriot plan FREE FOR LIFE!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Yearly rewards tier */}
                    <div className={`${bgTertiary} rounded-lg p-3`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${textMuted}`}>Every 10 referrals</span>
                        <span className="text-xs text-amber-400">1 Year Free</span>
                      </div>
                      <Progress value={((inviteStatus?.converted || 0) % 10) / 10 * 100} className={`h-2 ${isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-200'}`} />
                      <div className="flex justify-between mt-1">
                        <span className={`text-[10px] ${textMuted}`}>{(inviteStatus?.converted || 0) % 10}/10</span>
                        <span className={`text-[10px] ${textMuted}`}>{inviteStatus?.rewards_earned || 0} year(s) earned</span>
                      </div>
                    </div>
                    
                    {/* Lifetime tier */}
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-3 border border-purple-500/30">
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs ${textSecondary} flex items-center gap-1`}>
                          <Sparkles className="w-3 h-3 text-purple-400" />
                          50 referrals
                        </span>
                        <span className="text-xs text-purple-400 font-bold">FREE FOR LIFE</span>
                      </div>
                      <Progress value={Math.min((inviteStatus?.converted || 0) / 50 * 100, 100)} className={`h-2 ${isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-200'}`} />
                      <div className="flex justify-between mt-1">
                        <span className={`text-[10px] ${textMuted}`}>{inviteStatus?.converted || 0}/50</span>
                        <span className="text-[10px] text-purple-400">
                          {50 - (inviteStatus?.converted || 0)} more to lifetime!
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Referral Code Card */}
              <div className={`bg-gradient-to-br ${isDarkMode ? 'from-[#252540] to-[#1a1a2e]' : 'from-gray-100 to-white'} rounded-xl p-5 border border-[#6D4AFF]/30 mb-6`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${textMuted}`}>Your Referral Code</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyReferralCode}
                    className="text-[#6D4AFF] hover:text-[#8B5CF6] h-8"
                  >
                    {codeCopied ? <><CheckCircle className="w-4 h-4 mr-1" /> Copied!</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                  </Button>
                </div>
                <div className={`text-3xl font-bold ${textPrimary} tracking-wider text-center py-2`}>
                  {referralCode}
                </div>
                <p className={`text-xs ${textMuted} text-center mt-2`}>
                  Share this code with friends when they sign up
                </p>
              </div>

              {/* Previous Invites */}
              {inviteStatus?.invites?.length > 0 && (
                <div className="space-y-3">
                  <label className={`text-sm font-medium ${textSecondary} flex items-center gap-2`}>
                    <Users className="w-4 h-4" />
                    Your Invitations ({inviteStatus.invites.length})
                  </label>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {inviteStatus.invites.slice(0, 10).map((invite) => (
                      <div
                        key={invite.id}
                        className={`flex items-center justify-between p-3 ${bgTertiary} rounded-lg`}
                      >
                        <div>
                          <p className={`text-sm ${textPrimary}`}>{invite.invited_email}</p>
                          <p className={`text-xs ${textMuted}`}>
                            Sent {new Date(invite.sent_at).toLocaleDateString()}
                          </p>
                        </div>
                        {getStatusBadge(invite.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
